<template>
  <div class="gerenciar-modulos">
    <b-card no-body class="mb-1">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h4 class="mb-25">Gerenciar Módulos por Perfil</h4>
            <p class="mb-0 text-muted">
              Selecione um perfil e defina quais módulos ele pode acessar. Ao salvar, as permissões são substituídas pelas seleções abaixo.
            </p>
          </div>
          <b-badge v-if="sincronizado" variant="light-success" class="mt-50">
            <feather-icon icon="CheckCircleIcon" class="mr-25" />
            {{ novosCriados }} módulo(s) novo(s) registrado(s) automaticamente
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <b-overlay :show="loading" rounded="sm">
      <b-row>
        <b-col md="3">
          <b-card no-body class="sticky-top" style="top: 80px;">
            <b-card-header class="pb-50">
              <h6 class="mb-0">Perfis de Acesso</h6>
            </b-card-header>
            <b-list-group flush>
              <b-list-group-item
                v-for="perfil in perfis"
                :key="perfil.id"
                button
                class="d-flex align-items-center justify-content-between"
                :class="{ 'bg-primary text-white': perfilSelecionadoId === perfil.id }"
                @click="selecionarPerfil(perfil.id)"
              >
                <span>{{ perfil.perfil_acesso_nome }}</span>
                <b-badge
                  :variant="perfilSelecionadoId === perfil.id ? 'light' : 'light-primary'"
                  pill
                >
                  {{ contarPermissoes(perfil.id) }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>

        <b-col md="9">
          <template v-if="!perfilSelecionadoId">
            <b-card class="text-center py-3">
              <feather-icon icon="MousePointerIcon" size="48" class="text-muted mb-1" />
              <p class="text-muted">Selecione um perfil à esquerda para gerenciar os módulos.</p>
            </b-card>
          </template>

          <template v-else>
            <b-card no-body>
              <b-card-header>
                <div class="d-flex align-items-center justify-content-between w-100 flex-wrap">
                  <div class="d-flex align-items-center">
                    <h5 class="mb-0 mr-1">{{ perfilSelecionadoNome }}</h5>
                    <b-badge v-if="temAcessoTotal" variant="warning" class="mr-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
                      Acesso Total ativo
                    </b-badge>
                  </div>
                  <div class="d-flex align-items-center mt-50 mt-md-0">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="mr-50"
                      @click="marcarTodos(true)"
                    >
                      Marcar todos
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="mr-1"
                      @click="marcarTodos(false)"
                    >
                      Desmarcar todos
                    </b-button>
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="saving"
                      @click="salvar"
                    >
                      <b-spinner v-if="saving" small class="mr-50" />
                      <feather-icon v-else icon="SaveIcon" class="mr-50" size="14" />
                      Salvar
                    </b-button>
                  </div>
                </div>
              </b-card-header>

              <b-card-body>
                <b-alert v-if="temAcessoTotal" show variant="warning" class="mb-1">
                  <feather-icon icon="AlertTriangleIcon" class="mr-50" />
                  Este perfil tem <strong>Acesso Total</strong> ativo — todos os módulos ficam visíveis independente das seleções. Ao salvar, o acesso total será <strong>removido</strong> e somente os módulos marcados abaixo ficarão acessíveis.
                </b-alert>

                <b-input-group class="mb-1">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="SearchIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="busca"
                    placeholder="Filtrar módulos..."
                    debounce="300"
                  />
                </b-input-group>

                <div
                  v-for="grupo in gruposVisiveis"
                  :key="grupo.categoria"
                  class="mb-2"
                >
                  <div class="d-flex align-items-center mb-75">
                    <div class="categoria-linha flex-grow-1 mr-1"></div>
                    <span class="text-muted small font-weight-bold text-uppercase">{{ grupo.categoria }}</span>
                    <div class="categoria-linha flex-grow-1 ml-1"></div>
                  </div>
                  <b-row>
                    <b-col
                      md="6"
                      v-for="perm in grupo.permissoes"
                      :key="perm.id"
                      class="mb-75"
                    >
                      <div
                        class="modulo-item d-flex align-items-start p-75 rounded border"
                        :class="getSelecao(perm.id) ? 'border-primary bg-light-primary' : 'border-light'"
                      >
                        <b-form-checkbox
                          :checked="getSelecao(perm.id)"
                          :disabled="!permitePerfil(perm)"
                          switch
                          class="mt-0 mr-75 flex-shrink-0"
                          @change="val => setSelecao(perm.id, val)"
                        />
                        <div>
                          <div class="font-weight-bold small">{{ perm.subject === 'AssistenciaTecnica' ? 'Assistência Técnica' : perm.subject }}</div>
                          <div v-if="perm.subject === 'AssistenciaTecnica'" class="text-warning small">Exclusivo AdminDKP — nem Acesso Total libera outros perfis.</div>
                          <div class="text-muted" style="font-size: 0.8rem;">{{ perm.descricao }}</div>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div v-if="gruposVisiveis.length === 0" class="text-center py-2 text-muted">
                  Nenhum módulo encontrado para "{{ busca }}".
                </div>
              </b-card-body>
            </b-card>
          </template>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BCardHeader, BRow, BCol, BBadge, BListGroup, BListGroupItem,
  BFormCheckbox, BButton, BAlert, BInputGroup, BInputGroupPrepend, BFormInput,
  BOverlay, BSpinner,
} from 'bootstrap-vue';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SisModuloClient from '@/api/SistemaDKP/sis-modulo.client';

const CATEGORIAS_MAP = {
  'Assistência Técnica': ['AssistenciaTecnica'],
  Início: ['Home'],
  Sistema: ['Sistema', 'Usuarios', 'PerfilAcesso', 'Permissoes', 'Regras', 'CamposCustomizados', 'WorkFlow', 'WorkFlowAreas', 'WorkFlowUsuarios', 'WorkflowDirecionamento', 'GerenciarModulos'],
  'Operações': ['PedidosVenda', 'PedidosAprovacao', 'PainelPic', 'Pic', 'Operações', 'Operacao'],
  'Devoluções': ['Devolucoes', 'DevolucaoSaldo'],
  Budget: ['Budget', 'BudgetDashboard', 'BudgetLider'],
  Relatórios: ['Relatorios', 'RelatorioComissoes'],
  CRM: ['MenuCrmClientes', 'MenuCrmAgenda', 'MenuCrmOportunidades', 'MenuCrmCampanhas', 'MenuCrmAniversariantes', 'MenuCrmMarketing', 'MenuGamificacao'],
  Dashboard: ['DashboardEquipeVendas', 'DashboardCliente', 'AdminHanes'],
  'Portal do Cliente': ['ptc', 'Acesso_Dashboard', 'Acesso_Pedidos', 'Acesso_NotaFiscal', 'Acesso_Boleto', 'Acesso_Produtos', 'Acesso_Permissao', 'Acesso_CadastroCliente'],
  'Cliente DKP': ['MenuClienteDKP', 'ClienteDKP', 'TokenClienteDKP'],
  Clientes: ['GrupoComercial', 'Cliente', 'ClienteXTipoPedido', 'MenuCliente'],
  Produtos: ['Produto', 'Produtos', 'Item', 'GrupoEstoque', 'FamiliaComercial', 'FamiliaMaterial', 'UnidadeMedida'],
  'Apoio a Vendas': ['TipoTabela', 'Alcada', 'TipoPic', 'PicRegra', 'Representantes', 'RepresentantesXCondicaoPagamento', 'HierarquiaRepresentantes'],
  'Fiscal e Financeiro': ['CondicaoPagamento', 'NaturezaOperação', 'ClassificacaoFiscal', 'TipoPedido', 'TipoPedidoXEstabelecimento'],
  'Cadastros Gerais': ['Portador', 'Pais', 'Cidade', 'Uf', 'TipoEndereco', 'Banco', 'Cadastros', 'Gerais'],
  'Integração ERP': ['IntegracaoErp'],
  'Informações Organizacionais': ['UnidadeNegócio', 'Empresa', 'CanalVenda', 'Estabelecimento', 'Transportadora', 'InfoOrganizacionais', 'ApoioVendas'],
  Comunicado: ['TipoComunicado', 'Comunicado', 'MenuComunicado'],
  Imagens: ['Imagens', 'ImagensHome'],
  'Acesso Total': ['all'],
};

function getCategoria(subject) {
  for (const [cat, subjects] of Object.entries(CATEGORIAS_MAP)) {
    if (subjects.includes(subject)) return cat;
  }
  return 'Outros';
}

export default {
  name: 'GerenciarModulos',
  components: {
    BCard, BCardBody, BCardHeader, BRow, BCol, BBadge, BListGroup, BListGroupItem,
    BFormCheckbox, BButton, BAlert, BInputGroup, BInputGroupPrepend, BFormInput,
    BOverlay, BSpinner,
  },
  data() {
    return {
      loading: true,
      saving: false,
      sincronizado: false,
      novosCriados: 0,
      perfis: [],
      permissoes: [],
      selecoes: {},
      perfilSelecionadoId: null,
      busca: '',
      client: new SisModuloClient(),
    };
  },
  computed: {
    perfilSelecionadoNome() {
      const perfil = this.perfis.find(p => p.id === this.perfilSelecionadoId);
      return perfil ? perfil.perfil_acesso_nome : '';
    },
    temAcessoTotal() {
      const allPerm = this.permissoes.find(p => p.subject === 'all');
      return allPerm ? this.getSelecao(allPerm.id) : false;
    },
    permissoesFiltradas() {
      const q = (this.busca || '').toLowerCase().trim();
      if (!q) return this.permissoes;
      return this.permissoes.filter(p =>
        p.subject.toLowerCase().includes(q) || (p.descricao || '').toLowerCase().includes(q)
      );
    },
    gruposVisiveis() {
      const grupos = {};
      for (const perm of this.permissoesFiltradas) {
        const cat = getCategoria(perm.subject);
        if (!grupos[cat]) grupos[cat] = [];
        grupos[cat].push(perm);
      }
      return Object.entries(grupos)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([categoria, permissoes]) => ({ categoria, permissoes }));
    },
  },
  async mounted() {
    await this.syncManifest();
    await this.carregarMatriz();
  },
  methods: {
    async syncManifest() {
      try {
        const itens = this.extrairManifest();
        const { data } = await this.client.syncManifest(itens);
        if (data.criados > 0) {
          this.novosCriados = data.criados;
          this.sincronizado = true;
        }
      } catch (e) {
        // não bloqueia o carregamento
      }
    },
    extrairManifest() {
      const items = [];
      const seen = new Set();
      const processRoutes = (routes) => {
        for (const route of routes || []) {
          const meta = route.meta || {};
          if (meta.resource && !seen.has(meta.resource) && meta.resource !== 'Auth' && meta.resource !== 'all') {
            seen.add(meta.resource);
            items.push({
              action: meta.action || 'read',
              subject: meta.resource,
              descricao: meta.pageTitle || `Módulo: ${meta.resource}`,
            });
          }
          if (route.children) processRoutes(route.children);
        }
      };
      processRoutes(this.$router.options.routes || []);
      return items;
    },
    async carregarMatriz() {
      try {
        this.loading = true;
        const { data } = await this.client.obterMatriz();
        this.perfis = data.perfis;
        this.permissoes = data.permissoes;
        const novasSelecoes = {};
        for (const [perfilId, perms] of Object.entries(data.matriz)) {
          novasSelecoes[perfilId] = { ...perms };
        }
        this.selecoes = novasSelecoes;
      } finally {
        this.loading = false;
      }
    },
    selecionarPerfil(perfilId) {
      this.busca = '';
      this.perfilSelecionadoId = perfilId;
    },
    getSelecao(permId) {
      if (!this.perfilSelecionadoId) return false;
      if (!this.permitePerfil(this.permissoes.find(p => p.id === permId))) return false;
      return !!(this.selecoes[this.perfilSelecionadoId] || {})[permId];
    },
    setSelecao(permId, valor) {
      if (!this.perfilSelecionadoId) return;
      if (!this.permitePerfil(this.permissoes.find(p => p.id === permId))) return;
      const atual = { ...(this.selecoes[this.perfilSelecionadoId] || {}) };
      atual[permId] = valor;
      this.$set(this.selecoes, this.perfilSelecionadoId, atual);
    },
    marcarTodos(valor) {
      if (!this.perfilSelecionadoId) return;
      const novo = { ...(this.selecoes[this.perfilSelecionadoId] || {}) };
      for (const perm of this.permissoes) {
        novo[perm.id] = this.permitePerfil(perm) && valor;
      }
      this.$set(this.selecoes, this.perfilSelecionadoId, novo);
    },
    contarPermissoes(perfilId) {
      const perms = this.selecoes[perfilId] || {};
      return Object.values(perms).filter(Boolean).length;
    },
    permitePerfil(perm) {
      return !perm || perm.subject !== 'AssistenciaTecnica' || this.perfilSelecionadoNome === 'AdminDKP';
    },
    async salvar() {
      try {
        this.saving = true;
        const perfilId = this.perfilSelecionadoId;
        const permsAtual = this.selecoes[perfilId] || {};
        const permissaoIds = Object.entries(permsAtual)
          .filter(([id, v]) => v && this.permitePerfil(this.permissoes.find(p => p.id === Number(id))))
          .map(([k]) => parseInt(k));

        await this.client.atualizarPerfilPermissoes(perfilId, permissaoIds);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Permissões salvas!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `${permissaoIds.length} módulo(s) configurado(s) para o perfil "${this.perfilSelecionadoNome}".`,
          },
        });

        await this.carregarMatriz();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: e.message,
          },
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.categoria-linha {
  height: 1px;
  background: #e0e0e0;
}
.modulo-item {
  transition: all 0.15s ease;
  cursor: pointer;
  min-height: 56px;
}
.modulo-item:hover {
  border-color: #7367f0 !important;
}
.sticky-top {
  position: sticky;
}
</style>
